import type { Placement, PlacementSlot } from '../dataProvider/types';

/**
 * Display + derived-state helpers for the Placements surface. Both the lifecycle
 * and the overlap / share-of-voice are DERIVED and DISPLAYED, never enforced: a
 * placement carries no `is_live` flag and weight-sharing is legitimate, so the
 * surface surfaces the state honestly and blocks nothing (D-placements). Every
 * derivation reads the row(s) plus an injected clock, so it is deterministic in
 * tests.
 */

/** Human label per slot. The enum is the fixed backbone placement_slot set. */
export const SLOT_LABEL: Record<PlacementSlot, string> = {
  homepage_hero: 'Homepage hero',
  app_push_target: 'App push target',
  email_feature: 'Email feature',
};

/** The slot choices for the Create / Edit selectors (id + label). */
export const SLOT_CHOICES: Array<{ id: PlacementSlot; name: string }> = (
  Object.keys(SLOT_LABEL) as PlacementSlot[]
).map((slot) => ({ id: slot, name: SLOT_LABEL[slot] }));

const day = (iso: string): string => new Date(iso).toISOString().slice(0, 10);

/** "2026-05-01 to 2026-08-01", or "2026-05-01 to open" for an open-ended end. */
export const formatWindow = (row: Placement): string =>
  `${day(row.startsAt)} to ${row.endsAt == null ? 'open' : day(row.endsAt)}`;

/**
 * The derived lifecycle state. The window is half-open [startsAt, endsAt): a
 * placement is LIVE from the instant it starts and ENDED from the instant its
 * window closes, so the "End placement" action — which sets endsAt to now — moves
 * a row straight to `ended` (D-placements). An open-ended (endsAt null) placement
 * is live forever once it has started.
 *  - scheduled: now < startsAt.
 *  - ended: endsAt set and now >= endsAt.
 *  - live: otherwise.
 */
export type PlacementLifecycle = 'scheduled' | 'live' | 'ended';

export const placementLifecycle = (
  row: Placement,
  now: number,
): PlacementLifecycle => {
  if (now < Date.parse(row.startsAt)) return 'scheduled';
  if (row.endsAt != null && now >= Date.parse(row.endsAt)) return 'ended';
  return 'live';
};

export const LIFECYCLE_LABEL: Record<PlacementLifecycle, string> = {
  scheduled: 'Scheduled',
  live: 'Live',
  ended: 'Ended',
};

export const LIFECYCLE_COLOR: Record<
  PlacementLifecycle,
  'default' | 'success' | 'warning'
> = {
  scheduled: 'default',
  live: 'success',
  ended: 'warning',
};

/** Two windows overlap if each starts no later than the other ends (a null end
 *  is open-ended = +infinity). */
const windowsOverlap = (a: Placement, b: Placement): boolean => {
  const aStart = Date.parse(a.startsAt);
  const aEnd = a.endsAt == null ? Infinity : Date.parse(a.endsAt);
  const bStart = Date.parse(b.startsAt);
  const bEnd = b.endsAt == null ? Infinity : Date.parse(b.endsAt);
  return aStart <= bEnd && bStart <= aEnd;
};

/**
 * The overlap + share-of-voice for one placement, computed ONLY among genuinely
 * competing placements (same slot, same market, overlapping window — NOT the whole
 * band). Display-only: weight-sharing is legitimate, so this never blocks a
 * booking (D-placements). A placement alone in its slot has no competition and no
 * share figure (NOT a spurious 100%).
 */
export interface PlacementCompetition {
  /** Number of OTHER placements competing for this slot+market+overlapping window. */
  competitors: number;
  /** This placement's share of voice as a whole percentage, or null when alone. */
  sharePct: number | null;
}

export const placementCompetition = (
  row: Placement,
  all: readonly Placement[],
): PlacementCompetition => {
  const competing = all.filter(
    (p) =>
      p.id !== row.id &&
      p.slot === row.slot &&
      p.market === row.market &&
      windowsOverlap(p, row),
  );
  if (competing.length === 0) return { competitors: 0, sharePct: null };
  const totalWeight = [row, ...competing].reduce((sum, p) => sum + p.weight, 0);
  const sharePct =
    totalWeight === 0 ? 0 : Math.round((row.weight / totalWeight) * 100);
  return { competitors: competing.length, sharePct };
};

/** "Sole placement" when alone, else "Shares Homepage hero with 1 other (75% share)". */
export const formatCompetition = (
  competition: PlacementCompetition,
  slot: PlacementSlot,
): string => {
  if (competition.competitors === 0) return 'Sole placement';
  const others =
    competition.competitors === 1
      ? '1 other'
      : `${competition.competitors} others`;
  return `Shares ${SLOT_LABEL[slot]} with ${others} (${competition.sharePct}% share)`;
};
